import AlbumIcon from '@mui/icons-material/Collections'
import WarningIcon from '@mui/icons-material/WarningRounded'
import Chip from '@mui/material/Chip'
import { PPDialog } from 'components/PPDialog'
import { Fragment, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { setPinAiContentMode } from 'services/compose'
import {
  composerPinterestPostStatusSelector,
  PINTEREST_BOARD_NOT_SELECTED_MESSAGE_KEY,
  pinterestPostSelector,
  pinterestProfilesBoardsSelector
} from 'services/compose/selectors'
import { ComposerPinterestPost, PINTEREST_AI_SYNTHETIC_PERFORMER, PinterestBoard, PostDestination } from 'shared/types'
import PinterestBoardSelector from '../AlbumSelector/PinterestBoardSelector'
import { PinterestRefreshBoardsButton } from '../AlbumSelector/PinterestRefreshBoardsButton'
import { AiContentCheckbox } from './AiContentCheckbox'
import styles from './CustomizePostBlock.pcss'

interface ComposerPinterestPostOptionsProps {
  profiles: PostDestination[]
  className?: string
  boards?: Record<string, Array<{
    id: string
    name: string
    url?: string
    selected?: boolean
  }>>
  selectionDone?: boolean
  onSelectedBoardChange?: (ppid: string, board: PinterestBoard) => void
}

export function ComposerPinterestPostOptions(props: ComposerPinterestPostOptionsProps) {
  const dispatch = useDispatch()
  const pin = useSelector(pinterestPostSelector) as ComposerPinterestPost
  const composerBoards = useSelector(pinterestProfilesBoardsSelector)
  const boards = props.boards || composerBoards
  const pinterestPostStatus = useSelector(composerPinterestPostStatusSelector)
  const [popupOpen, setPopupOpen] = useState(false)

  // EXPL: "AI modified" is the composer-level AI content flag - the pin only adds the synthetic performer mode
  const onSyntheticPerformerChange = (value: boolean) => {
    dispatch(setPinAiContentMode(value ? PINTEREST_AI_SYNTHETIC_PERFORMER : undefined))
  }

  const closePopup = () => {
    setPopupOpen(false)
  }

  const openPopup = () => {
    setPopupOpen(true)
  }

  const isBoardsSelectionComplete = props.selectionDone
    || (!pinterestPostStatus.isEmpty && !pinterestPostStatus.caption.errors.includes(PINTEREST_BOARD_NOT_SELECTED_MESSAGE_KEY))

  return (
    <div className={props.className} data-testid="composer-pinterest-post-options">
      <div className={styles['option-box']}>
        <AiContentCheckbox
          checked={pin.aiContentMode === PINTEREST_AI_SYNTHETIC_PERFORMER}
          id="pinterest-ai-synthetic-performer"
          label={<FormattedMessage id="composer.labels.ai-synthetic-performer" />}
          onChange={onSyntheticPerformerChange}
        />
      </div>
      {props.profiles.length === 1 && (
        <div className={`${styles['option-box']} ${styles.inline}`}>
          <PinterestBoardSelector
            profile={props.profiles[0]}
            boards={boards[props.profiles[0].ppid]}
            onSelectedBoardChange={props.onSelectedBoardChange}
          />
          <PinterestRefreshBoardsButton />
        </div>
      )}
      {props.profiles.length > 1 && (
        <div className={styles['option-box']}>
          <Chip
            label={
              <FormattedMessage
                id={isBoardsSelectionComplete ? 'composer.labels.selected-boards-change' : 'composer.labels.select-multiple-boards'}
              />
            }
            avatar={isBoardsSelectionComplete ? (
              <AlbumIcon className={styles['icon-album']} />
            ) : (
              <WarningIcon className={styles['icon-album']} />
            )}
            classes={{ root: styles.chip, label: styles.label }}
            onClick={openPopup}
          />
        </div>
      )}
      <PPDialog
        open={popupOpen}
        onClose={closePopup}
        size="lg"
        title={
          <Fragment>
            <FormattedMessage id="composer.labels.select-multiple-boards" />
            <PinterestRefreshBoardsButton />
          </Fragment>
        }
      >
        <div className={styles['popup-body']}>
          <div className={styles['albums-popup-content']}>
            {props.profiles.map(p => (
              <div key={p.id} className={styles['option-box']}>
                <PinterestBoardSelector
                  profile={p}
                  boards={boards[p.ppid]}
                  raised
                  onSelectedBoardChange={props.onSelectedBoardChange}
                />
              </div>
            ))}
          </div>
        </div>
      </PPDialog>
    </div>
  )
}
